import { ThemeColorName } from '@examdojo/styles';
import type { Board, BoardAttributes } from 'jsxgraph';

import type { TransitionProfile } from '../../model/widget-action.model';
import type { BehaviorConfig } from '../../model/widget-behavior.model';

// =============================================================================
// JSXGRAPH LIBRARY TYPES
// =============================================================================

export type JsxgraphBoard = Board;
export type JsxgraphStatic = typeof import('jsxgraph');
export type JsxgraphBoardOptions = Partial<BoardAttributes>;

// =============================================================================
// THEME (colors, typography, spacing - raw values only)
// =============================================================================

/**
 * Theme color palette for JSXGraph elements.
 */
export interface ThemeColors {
  /** Color for axis lines */
  axis: string;
  /** Color for grid lines */
  grid: string;
  /** Background color for the board container */
  background: string;
  /** Border color for the board container */
  border: string;
  /** Secondary text color (for labels, tick marks) */
  textSecondary: string;
  /** Array of feature colors for data visualization */
  feature: string[];
  /** Shading colors keyed by theme color name */
  shading: Record<ThemeColorName, string>;
}

/**
 * Theme typography settings.
 */
export interface ThemeTypography {
  fontFamily: string;
  fontSize: {
    base: number;
    small: number;
    label: number;
  };
  fontWeight: {
    normal: number;
    medium: number;
    bold: number;
  };
}

/**
 * Theme spacing and visual settings.
 */
export interface ThemeSpacing {
  borderRadius: string;
  labelPadding: string;
  labelOffset: [number, number];
  sliderLength: number;
}

/**
 * Label color helpers for different label variants.
 */
export interface LabelColors {
  white: string;
  bgLight: (color: ThemeColorName) => string;
  textDark: (color: ThemeColorName) => string;
  textDarker: (color: ThemeColorName) => string;
}

/**
 * Complete theme configuration for JSXGraph.
 * Contains only raw values - no JSXGraph API options.
 */
export interface JsxgraphTheme {
  colors: ThemeColors;
  typography: ThemeTypography;
  spacing: ThemeSpacing;
  labelColors: LabelColors;
}

/**
 * Result of label style computation.
 */
export interface LabelStyleResult {
  backgroundColor: string;
  textColor: string;
}

// =============================================================================
// FEATURE CONFIGURATION (high-level widget config)
// =============================================================================

/**
 * Variant options for label styling.
 * - 'primary': white text on feature color 500 background
 * - 'secondary': feature color 600 text on feature color 100 background (default)
 * - 'tertiary': feature color 700 text, no background
 */
export type LabelVariant = 'primary' | 'secondary' | 'tertiary';

/**
 * Format options for feature labels.
 */
export type LabelFormat = 'name' | 'coordinate' | 'nameAndCoordinate';

/**
 * Configuration for a feature label.
 */
export interface LabelConfig {
  show?: boolean;
  format?: LabelFormat;
  variant?: LabelVariant;
  color?: ThemeColorName;
}

/**
 * High-level axes feature configuration.
 * Controls what axis features to display.
 */
export interface AxesFeatureConfig {
  enabled: boolean;
  xAxis?: boolean;
  yAxis?: boolean;
  ticks?: boolean;
  labels?: boolean;
}

/**
 * Display configuration for JSXGraph widgets.
 */
export interface DisplayConfig {
  /** Show/hide grid */
  grid?: boolean;
  axes?: AxesFeatureConfig | boolean;
  boundingbox?: [number, number, number, number];
}

/**
 * Slider feature configuration.
 */
export interface SlidersFeatureConfig {
  enabled: boolean;
  parameters: string[];
  position?: 'top' | 'right' | 'bottom' | 'left';
  snapWidth?: number;
  ranges?: Record<string, [number, number]>;
  individualStyles?: Record<string, Partial<SliderStyleOverrides>>;
}

/**
 * Interaction feature configuration.
 */
export interface InteractionFeatureConfig {
  enabled: boolean;
  draggable?: boolean;
  zoomable?: boolean;
  pannable?: boolean;
  padding?: number;
}

/**
 * JSXGraph-specific features that go inside behavior.features.
 */
export interface JsxgraphFeatures extends Record<string, unknown> {
  sliders?: SlidersFeatureConfig | boolean;
  interaction?: InteractionFeatureConfig | boolean;
}

export type JsxgraphActionType = 'FOCUS_REGION' | 'TRANSFORM_VIEW' | 'PATCH_STYLE' | 'HIGHLIGHT' | 'RESET_VIEW';

/**
 * JSXGraph-specific behavior configuration.
 */
export type JsxgraphBehaviorConfig<
  ActionTarget extends string = string,
  ActionType extends string | never = never,
> = BehaviorConfig<ActionTarget, ActionType | JsxgraphActionType, JsxgraphFeatures>;

// =============================================================================
// STYLING OVERRIDES (optional customization)
// =============================================================================

/**
 * Optional line style overrides.
 */
export interface LineStyleOverrides {
  colorName?: ThemeColorName;
  strokeColor?: string;
  strokeWidth?: number;
  dash?: number;
}

/**
 * Optional slider style overrides.
 */
export interface SliderStyleOverrides {
  strokeColor?: string;
  strokeWidth?: number;
  fillColor?: string;
  highlightStrokeColor?: string;
  snapWidth?: number;
  baseline?: {
    strokeColor?: string;
    strokeWidth?: number;
  };
  highline?: {
    strokeColor?: string;
    strokeWidth?: number;
  };
}

// =============================================================================
// DEFAULTS
// =============================================================================

export const DEFAULT_AXES_CONFIG: AxesFeatureConfig = {
  enabled: true,
  xAxis: true,
  yAxis: true,
  ticks: true,
  labels: true,
};

export const DEFAULT_DISPLAY_CONFIG: DisplayConfig = {
  grid: false,
  axes: DEFAULT_AXES_CONFIG,
  boundingbox: [-10, 10, 10, -10],
};

export const DEFAULT_SLIDERS_CONFIG: SlidersFeatureConfig = {
  enabled: false,
  parameters: [],
  position: 'top',
  snapWidth: 0.1,
};

export const DEFAULT_INTERACTION_CONFIG: InteractionFeatureConfig = {
  enabled: true,
  draggable: true,
  zoomable: true,
  pannable: true,
};

export const DEFAULT_JSXGRAPH_FEATURES: JsxgraphFeatures = {
  sliders: DEFAULT_SLIDERS_CONFIG,
  interaction: DEFAULT_INTERACTION_CONFIG,
};

export const DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG: JsxgraphBehaviorConfig = {
  features: DEFAULT_JSXGRAPH_FEATURES,
};

export const DEFAULT_BOARD_OPTIONS: JsxgraphBoardOptions = {
  showCopyright: false,
  showNavigation: false,
  axis: false,
  grid: false,
  keepAspectRatio: false,
  boundingbox: [-5, 5, 5, -5],
  showInfobox: false,
};

// =============================================================================
// DEFAULT TRANSITIONS
// =============================================================================

export const DEFAULT_TRANSITIONS: Record<string, TransitionProfile> = {
  instant: { duration: 0, easing: 'linear', type: 'none' },
  fast: { duration: 150, easing: 'easeOut', type: 'numeric' },
  default: { duration: 400, easing: 'easeInOut', type: 'numeric' },
  slow: { duration: 800, easing: 'easeInOut', type: 'numeric' },
  camera: { duration: 400, easing: 'easeInOut', type: 'camera' },
  bounce: { duration: 600, easing: 'bounce', type: 'numeric' },
};

// =============================================================================
// UTILITIES
// =============================================================================

export function normalizeConfig<T extends { enabled: boolean }>(config: T | boolean | undefined, defaults: T): T {
  if (config === undefined || config === false) {
    return { ...defaults, enabled: false };
  }
  if (config === true) {
    return { ...defaults, enabled: true };
  }
  return { ...defaults, ...config };
}
